import { useQuery } from '@tanstack/react-query'
import { getAdBalance } from '@/api/balance'

interface Props {
  channelUUID: string
}

function formatRub(value: number): string {
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 0 })
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

export function BalanceCard({ channelUUID }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['adBalance', channelUUID],
    queryFn: () => getAdBalance(channelUUID),
    enabled: !!channelUUID,
    staleTime: 60_000,
    retry: false,
  })

  const noAuth = data?.error === 'NO_YANDEX_AUTH'
  const unavailable = data?.error === 'YANDEX_UNAVAILABLE'
  const hasAmount = data && typeof data.amount === 'number'
  const amount = hasAmount ? formatRub(data!.amount as number) : '—'
  const days = data?.daysRemaining ?? null

  return (
    <div className="balance-card">
      <div className="balance-eyebrow">Рекламный бюджет</div>
      <div className="balance-value">
        {isLoading ? '…' : amount}
        <em>₽</em>
      </div>
      <div className="balance-meta">
        {noAuth ? (
          <span>Яндекс.Директ не подключён</span>
        ) : unavailable ? (
          <span>Сервис временно недоступен</span>
        ) : days !== null && days > 0 ? (
          <>
            Хватит примерно на <b>{days} {plural(days, 'день', 'дня', 'дней')}</b> при текущем темпе
          </>
        ) : days === 0 ? (
          <b style={{ color: '#FF6B6B' }}>Бюджет закончится сегодня</b>
        ) : (
          <span>Прогноз недоступен</span>
        )}
      </div>
      <div className="balance-actions">
        {noAuth ? (
          <button className="balance-btn balance-btn-primary">Подключить Я.Директ</button>
        ) : (
          <a
            className="balance-btn balance-btn-primary"
            href="https://direct.yandex.ru/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Пополнить
          </a>
        )}
      </div>

      <style>{`
        .balance-card {
          background: var(--tg-ink, #0a0a0f);
          color: white;
          border-radius: 16px;
          padding: 22px 24px;
          position: relative;
          overflow: hidden;
        }
        .balance-card::after {
          content: '';
          position: absolute;
          right: -60px;
          top: -60px;
          width: 180px;
          height: 180px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0,128,255,0.45) 0%, transparent 70%);
          pointer-events: none;
        }
        .balance-eyebrow {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.5);
          margin-bottom: 12px;
          position: relative;
          z-index: 1;
        }
        .balance-value {
          font-weight: 800;
          font-size: 36px;
          line-height: 1;
          letter-spacing: -0.025em;
          color: white;
          font-variant-numeric: tabular-nums;
          margin-bottom: 4px;
          position: relative;
          z-index: 1;
        }
        .balance-value em {
          font-family: 'Instrument Serif', serif;
          font-style: italic;
          font-weight: 400;
          font-size: 22px;
          color: rgba(255, 255, 255, 0.6);
          margin-left: 4px;
        }
        .balance-meta {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          font-weight: 500;
          margin-bottom: 18px;
          position: relative;
          z-index: 1;
        }
        .balance-meta b {
          color: white;
          font-weight: 600;
        }
        .balance-actions {
          display: flex;
          gap: 8px;
          position: relative;
          z-index: 1;
        }
        .balance-btn {
          flex: 1;
          padding: 10px 12px;
          border-radius: 10px;
          border: 0;
          font-family: inherit;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          transition: background 150ms;
          text-align: center;
          text-decoration: none;
          display: inline-block;
        }
        .balance-btn-primary {
          background: var(--tg-blue, #0080ff);
          color: white;
          box-shadow: 0 1px 0 0 rgba(255, 255, 255, 0.16) inset;
        }
        .balance-btn-primary:hover {
          background: var(--tg-blue-hover, #0066cc);
        }
        .balance-btn-ghost {
          background: rgba(255, 255, 255, 0.08);
          color: white;
        }
        .balance-btn-ghost:hover {
          background: rgba(255, 255, 255, 0.16);
        }
      `}</style>
    </div>
  )
}
